import { ChangeEvent, useState } from 'react';
import { styled } from '@stitches/configed';
import Layout from './Layout';
import { Result } from '../Common/Result';
import CheckCircleIcon from '../Common/Icons/CheckCirleIcon';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Input } from 'antd';
import { Title } from './Login';

const RowCheck = styled('div', {
  display: 'flex',
  alignItems: 'center',
  uGap: 4,
  svg: {
    color: '#D9DCE1',
  },
  variants: {
    passed: {
      true: {
        svg: {
          color: '#17AFA7',
        },
      },
    },
  },
});

type Props = {
  token: string;
  onFinish: (
    values: any,
    token: string,
  ) => Promise<{
    success?: boolean;
    errors?: { field: string; errors: string[] }[];
  }>;
};
const ResetPassword = ({ onFinish, token }: Props) => {
  const [form] = Form.useForm();
  const [isSubmiting, setIsSubmiting] = useState(false);

  const [showResult, setShowResult] = useState(false);
  const [isSixChar, setIsSixChar] = useState(false);
  const [isHasUppercase, setIsHasUppercase] = useState(false);
  const [isHasNumber, setIsHasNumber] = useState(false);
  const [isHasSpecialChar, setIsHasSpecicalChar] = useState(false);
  const navigate = useNavigate();

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    if (password.length >= 6) {
      setIsSixChar(true);
    } else {
      setIsSixChar(false);
    }
    if (/[A-Z]/.test(password)) {
      setIsHasUppercase(true);
    } else {
      setIsHasUppercase(false);
    }
    /[0-9]/.test(password) ? setIsHasNumber(true) : setIsHasNumber(false);
    /[!@#$%^&*(),.?":{}|<>]/.test(password)
      ? setIsHasSpecicalChar(true)
      : setIsHasSpecicalChar(false);
  };

  if (showResult) {
    return (
      <Layout>
        <Result
          type="success"
          title="Khôi phục mật khẩu thành công."
          message="Từ bây giờ bạn sẽ sử dụng email và mật khẩu mới để đăng nhập hệ thống."
        >
          <Button block type="primary" onClick={() => navigate('/login')}>
            Đăng nhập tài khoản
          </Button>
        </Result>
      </Layout>
    );
  }
  return (
    <Layout>
      <Form
        layout="vertical"
        onFinish={async (value) => {
          setIsSubmiting(true);
          const res = await onFinish(value, token);
          if (res.success) {
            setShowResult(true);
          }
          if (res.errors) {
            const arr = res.errors.map((x) => {
              return {
                name: x.field,
                errors: x.errors,
              };
            });
            form.setFields(arr);
            form.scrollToField(arr[0].name);
          }
          setIsSubmiting(false);
        }}
        validateTrigger="onSubmit"
      >
        <Title>Khôi phục mật khẩu</Title>
        <Form.Item
          label="Mật khẩu mới"
          name="password"
          rules={[
            { required: true, message: 'Vui lòng nhập mật khẩu mới!' },
            {
              min: 6,
              message: 'Vui lòng nhập ít nhất 6 ký tự!',
            },
          ]}
        >
          <Input.Password
            placeholder="Nhập mật khẩu mới"
            onChange={handlePasswordChange}
          />
        </Form.Item>
        <Form.Item
          label="Xác nhận mật khẩu mới"
          name="passwordConfirmation"
          dependencies={['password']}
          rules={[
            { required: true, message: 'Vui lòng nhập lại mật khẩu mới!' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error('Hai mật khẩu đã nhâp không khớp!'),
                );
              },
            }),
          ]}
        >
          <Input.Password placeholder="Nhập mật lại khẩu mới" />
        </Form.Item>

        <Form.Item>
          Mật khẩu có độ bảo mật cao, nên có:
          <RowCheck passed={isSixChar}>
            <CheckCircleIcon />
            <span>Ít nhất 6 kí tự</span>
          </RowCheck>
          <RowCheck passed={isHasUppercase}>
            <CheckCircleIcon />
            <span>Có ký tự viết hoa (từ A - Z)</span>
          </RowCheck>
          <RowCheck passed={isHasNumber}>
            <CheckCircleIcon />
            <span>Có ký tự số (từ 0 - 9)</span>
          </RowCheck>
          <RowCheck passed={isHasSpecialChar}>
            <CheckCircleIcon />
            <span>Có ký tự đặt biệt (? * @...)</span>
          </RowCheck>
        </Form.Item>
        <Form.Item>
          <Button block loading={isSubmiting} type="primary" htmlType="submit">
            Cập nhật
          </Button>
        </Form.Item>
      </Form>
    </Layout>
  );
};

export default ResetPassword;
